import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TbCheck, TbHourglassHigh, TbKey, TbPlus, TbX } from 'react-icons/tb';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { useGetClaimsByOwner } from '@/api/hooks/useClaim';
import { useFetchRestaurantsByOwnerMutation } from '@/api/hooks/useRestaurant';
import { SimpleRestaurantResponse } from '@/api/lib/restaurant';
import { useAuthContext } from '@/contexts/auth';

import { PastilleNumber } from '@/components/atoms/PastilleNumber/PastilleNumber';
import { Spinner } from '@/components/atoms/Spinner';
import { Typography } from '@/components/atoms/Typography';
import { ClaimHomePageItem } from '@/components/molecules/ClaimHomePageItem';
import { RestaurantHomePageItem } from '@/components/molecules/RestaurantHomePageItem';

export const HomePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthContext();
  const { t } = useTranslation();
  const [restaurantsData, setRestaurantsData] = useState<SimpleRestaurantResponse[]>([]);
  const [searchTerm, setSearchTerm] = useState('');

  const [currentTab, setCurrentTab] = useState<'accepted' | 'pending' | 'rejected' | 'claim'>('accepted');

  const { data: claimsData, isLoading: isClaimsLoading } = useGetClaimsByOwner();

  const {
    data,
    isLoading,
    error,
    mutate: fetchRestaurantsByOwner
  } = useFetchRestaurantsByOwnerMutation({
    onSuccess: (data) => {
      setRestaurantsData(data);
    }
  });

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const tab = query.get('tab');
    if (tab === 'pending' || tab === 'rejected' || tab === 'accepted' || tab === 'claim') {
      setCurrentTab(tab);
    }
  }, [location.search]);

  useEffect(() => {
    fetchRestaurantsByOwner({ search: '' });
  }, [fetchRestaurantsByOwner]);

  useEffect(() => {
    if (!isLoading && data && data.length === 0) {
      navigate('/proposal-restaurant');
    }
  }, [isLoading, data, navigate]);

  if (error) {
    return <div>Erreur lors du chargement des restaurants</div>;
  }

  const handleTabChange = (tab: 'accepted' | 'pending' | 'rejected' | 'claim') => {
    setCurrentTab(tab);
    navigate(`/?tab=${tab}`);
  };

  const filteredRestaurants = restaurantsData
    .filter((restaurant) => {
      if (currentTab === 'accepted') {
        return restaurant.validationStatus === 'ACCEPTED';
      } else if (currentTab === 'pending') {
        return restaurant.validationStatus === 'PENDING';
      } else if (currentTab === 'rejected') {
        return restaurant.validationStatus === 'REJECTED';
      }
      return false;
    })
    .filter((restaurant) => restaurant.name.toLowerCase().includes(searchTerm.toLowerCase()));

  const classTabButton =
    'inline-block w-full flex h-full items-center justify-center gap-2 p-4 border text-gray-900 bg-gray-100 border-gray-200 focus:ring-0 hover:text-white hover:bg-primary/60';

  return (
    <section className="bg-primary">
      <section className="mb-5 -mt-14 pt-32 md:pt-44 p-8 md:p-14 flex bg-primary w-full flex-col relative">
        <Typography variant="heroTitle" align="center" color="white">
          Quel restaurant souhaitez-vous <strong>gérer</strong> ?
        </Typography>
      </section>

      <section className="bg-white md:rounded-t-[60px] pt-10 rounded-t-[30px] md:pt-20 w-full ">
        {user && user.isSupport && (
          <Typography variant="h1" align="center">
            <strong>{restaurantsData.length}</strong> restaurants au total
          </Typography>
        )}
        <section className="md:max-w-6xl mx-auto p-5 md:p-10">
          <ul className="text-sm font-medium text-center text-gray-500 rounded-xl shadow flex">
            <li className="w-full focus-within:z-10">
              <button
                onClick={() => handleTabChange('accepted')}
                className={classNames('border-r rounded-s-xl text-md', currentTab === 'accepted' && 'bg-primary text-white', classTabButton)}
              >
                <TbCheck size={20} />
                <strong className="hidden md:block">Actifs</strong>
                <PastilleNumber
                  currentTab={currentTab === 'accepted'}
                  number={restaurantsData.filter((restaurant) => restaurant.validationStatus === 'ACCEPTED').length}
                />
              </button>
            </li>
            <li className="w-full focus-within:z-10">
              <button
                onClick={() => handleTabChange('pending')}
                className={classNames('border-r text-md', currentTab === 'pending' && 'bg-primary text-white', classTabButton)}
              >
                <TbHourglassHigh size={20} />
                <strong className="hidden md:block">En attente de validation</strong>
                <PastilleNumber
                  currentTab={currentTab === 'pending'}
                  number={restaurantsData.filter((restaurant) => restaurant.validationStatus === 'PENDING').length}
                />
              </button>
            </li>

            <li className="w-full focus-within:z-10">
              <button
                onClick={() => handleTabChange('claim')}
                className={classNames('border-r text-md', currentTab === 'claim' && 'bg-primary text-white', classTabButton)}
              >
                <TbKey size={20} />
                <strong className="hidden md:block">Transfert de propriété</strong>
                <PastilleNumber currentTab={currentTab === 'claim'} number={claimsData?.length} />
              </button>
            </li>

            <li className="w-full focus-within:z-10">
              <button
                onClick={() => handleTabChange('rejected')}
                className={classNames('rounded-e-xl border-s-0 text-md', currentTab === 'rejected' && 'bg-primary text-white', classTabButton)}
              >
                <TbX size={20} /> <strong className="hidden md:block">Refusés</strong>
                <PastilleNumber
                  currentTab={currentTab === 'rejected'}
                  number={restaurantsData.filter((restaurant) => restaurant.validationStatus === 'REJECTED').length}
                />
              </button>
            </li>
          </ul>

          <Typography variant="h2" marginClassName="my-5">
            Sélectionnez un restaurant
          </Typography>

          <input
            type="text"
            placeholder="Rechercher un restaurant..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-primary focus:outline-none focus:ring-0"
          />

          {isLoading || isClaimsLoading ? (
            <div className="flex flex-auto justify-center items-center h-48">
              <Spinner reverse={true} big />
            </div>
          ) : (
            <>
              {currentTab === 'claim' && data ? (
                <div className="gap-3 pb-14 md:my-8 my-4 grid lg:grid-cols-4 grid-cols-2 md:grid-cols-3 xl:grid-cols-5">
                  {claimsData?.map((claim) => <ClaimHomePageItem key={claim.id} isSupport={!!(user && user.isSupport)} claim={claim} restaurantsData={data} />)}
                </div>
              ) : (
                <div className="gap-3 pb-14 md:my-8 my-4 grid lg:grid-cols-4 grid-cols-2 md:grid-cols-3 xl:grid-cols-5">
                  {filteredRestaurants?.map((restaurant) => (
                    <RestaurantHomePageItem key={restaurant.id} isSupport={!!(user && user.isSupport)} restaurant={restaurant} />
                  ))}
                </div>
              )}
            </>
          )}

          <Link
            to="/proposal-restaurant"
            type="button"
            className="hover:cursor-pointer w-full mx-auto flex gap-2 bg-primary text-white hover:border-primary duration-200 justify-center ease-in-out hover:text-white px-5 py-3 border rounded-lg hover:bg-primary/70 items-center border-gray-300"
          >
            <TbPlus className="text-xl" />
            {t('navMenu.restaurantAdd')}
          </Link>
        </section>
      </section>
    </section>
  );
};
